import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { lecturesAPI } from '../../services/lectureService';
import LectureFilter from './LectureFilter';
import LectureGrid from './LectureGrid';
import LectureUploadDialog from './LectureUploadDialog';

const Lectures = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tId= localStorage.getItem('employeeId')
  const toast = useRef(null);
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const fetchedRef = useRef(false);

  const BASE_URL = process.env.REACT_APP_API_URL;

  const getThumbnailUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    const cleanBase = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
    return `${cleanBase}/${cleanPath}`;
  };

  const getBadgeText = (contentType) => {
    if (!contentType) return 'FILE';
    const type = contentType.toLowerCase();
    if (type.includes('pdf')) return 'PDF';
    if (type.includes('presentation') || type.includes('powerpoint') || type.includes('ppt')) return 'PowerPoint';
    if (type.includes('word') || type.includes('document') || type.includes('docx')) return 'Word';
    if (type.includes('spreadsheet') || type.includes('excel') || type.includes('sheet')) return 'Excel';
    if (type.includes('video')) return 'Video';
    if (type.includes('audio')) return 'Audio';
    if (type.includes('image')) return 'Image';
    if (type.includes('text')) return 'Text';
    return type.split('/')[1]?.toUpperCase() || 'FILE';
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    lectureFile: null,
    thumbnail: null,
  });

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchLectures();
    }
  }, []);

  const fetchLectures = async () => {
    const employeeId = localStorage.getItem('employeeId')
    setLoading(true);
    try {
      const response = await lecturesAPI.getLecturesById(employeeId);
      setLectures(response.data.lectures || []);
    } catch (err) {
      console.error('Error fetching lectures:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim() || !formData.lectureFile) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please fill in all required fields' });
      return;
    }

    
    let lectureObject = {
      title: formData.title,
      description: formData.description,
      teacherId: tId,
      lectureFile: formData.lectureFile,
      thumbnail: formData.thumbnail
    };

    try {
      setLoading(true);
      toast.current?.show({ severity: 'info', summary: 'Uploading', detail: 'Please wait, uploading your lecture...' });

      const response = await lecturesAPI.uploadLecture(lectureObject);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: response.data.message });
      setFormData({ title: '', description: '', lectureFile: null, thumbnail: null });

      setTimeout(() => {
        setShowUploadModal(false);
        fetchLectures();
      }, 1500);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to upload lecture' });
    } finally {
      setLoading(false);
    }
  };

  const handleLectureView = (lecture) => {
    navigate(`/lectures/${lecture.id}`, { state: { lecture } });
  };

  const confirmDelete = (lectureId) => {
    confirmDialog({
      message: 'Are you sure you want to delete this lecture? This action cannot be undone.',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md mx-2',
      rejectClassName: 'bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-2 rounded-lg transition-all mx-2',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'No, Keep it',
      accept: () => handleDeleteLecture(lectureId),
    });
  };

  const handleDeleteLecture = async (lectureId) => {
    try {
      setLoading(true);
      const response = await lecturesAPI.deleteLecture(lectureId);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: response.data.message || 'Lecture deleted successfully' });
      fetchLectures();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to delete lecture' });
    } finally {
      setLoading(false);
    }
  };

  const filteredLectures = lectures.filter((lecture) => {
    const query = searchQuery.toLowerCase();
    return (
      lecture.title.toLowerCase().includes(query) ||
      lecture.description.toLowerCase().includes(query)
    );
  });

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <LectureFilter 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setShowUploadModal={setShowUploadModal}
      />

      <LectureGrid 
        loading={loading}
        lectures={lectures}
        filteredLectures={filteredLectures}
        getThumbnailUrl={getThumbnailUrl}
        confirmDelete={confirmDelete}
        handleLectureView={handleLectureView}
        getBadgeText={getBadgeText}
      />

      <LectureUploadDialog 
        visible={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        formData={formData}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  );
};

export default Lectures;
